import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Notification } from "@carbon/icons-react";
import { callMongoApi } from "../../services/mongoApiService";

const POLL_MS = 60000;
const PAGE_SIZE = 20;

const timeAgo = (iso) => {
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (isNaN(diff)) return "";
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hr ago`;
  return `${Math.floor(diff / 86400)} day(s) ago`;
};

export default function NotificationBell() {
  const navigate = useNavigate();
  const wrapRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);

const sentinelRef = useRef(null);
const [page, setPage] = useState(1);
const [hasMore, setHasMore] = useState(true);
const [loadingMore, setLoadingMore] = useState(false);
const [total, setTotal] = useState(0);
const [serverUnread, setServerUnread] = useState(0);
  const [readIds, setReadIds] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("read_notification_ids")) || [];
    } catch {
      return [];
    }
  });

  const isRead = (n) => n.isRead === true || readIds.includes(n.notificationId);
  const localUnread = items.filter((n) => !isRead(n)).length;
const unread = Math.max(serverUnread, 0) > 0 ? serverUnread : localUnread;

const markRead = async (ids) => {
  if (!ids || ids.length === 0) return;

  // 1. Optimistic local update
  const next = Array.from(new Set([...readIds, ...ids]));
  setReadIds(next);
  localStorage.setItem("read_notification_ids", JSON.stringify(next));
  setServerUnread((prev) => Math.max(0, prev - ids.length));

  // 2. Persist to backend
  try {
    await callMongoApi("PUT", "/device/notifications/read-all?isAdmin=true", {
      notificationIds: ids,
    });
  } catch (err) {
    console.error("Failed to mark as read on server:", err);
    // Optional: rollback on failure
    // fetchNotifications(1, { append: false });
  }
};

const fetchNotifications = useCallback(
  async (targetPage = 1, { append = false } = {}) => {
    if (append) setLoadingMore(true);
    try {
      const res = await callMongoApi(
        "GET",
        `/device/notifications?page=${targetPage}&limit=${PAGE_SIZE}&isAdmin=true`
      );
      const payload = res?.data || {};
      const list = payload.notifications || [];
      const pagination = payload.pagination || {};
      const stats = payload.stats || {};

      setItems((prev) => (append ? [...prev, ...list] : list));
      setPage(targetPage);
      setTotal(pagination.total ?? list.length);
      setHasMore(
        pagination.totalPages != null
          ? targetPage < pagination.totalPages
          : list.length === PAGE_SIZE
      );
      if (stats.unread != null) setServerUnread(stats.unread);
    } catch (err) {
      console.error("Failed to load notifications:", err);
      if (!append) setHasMore(false);
    } finally {
      if (append) setLoadingMore(false);
    }
  },
  []
);

  // load once + poll
useEffect(() => {
  fetchNotifications(1, { append: false });                    
  const t = setInterval(
    () => fetchNotifications(1, { append: false }),              
    POLL_MS
  );
  return () => clearInterval(t);
}, [fetchNotifications]);

// 👇 ADD THIS ENTIRE BLOCK
// Infinite scroll: load next page when sentinel appears
useEffect(() => {
  if (!open || !hasMore || loadingMore) return;
  const el = sentinelRef.current;
  if (!el) return;
  const io = new IntersectionObserver(
    (entries) => {
      if (entries[0].isIntersecting && hasMore && !loadingMore) {
        fetchNotifications(page + 1, { append: true });
      }
    },
    { root: el.parentElement, rootMargin: "80px" }
  );
  io.observe(el);
  return () => io.disconnect();
}, [open, hasMore, loadingMore, page, fetchNotifications]);

const handleScroll = (e) => {
  const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
  if (hasMore && !loadingMore && scrollHeight - scrollTop - clientHeight < 100) {
    fetchNotifications(page + 1, { append: true });
  }
};

  // close when clicking outside
  useEffect(() => {
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const handleItemClick = (n) => {
    markRead([n.notificationId]);
    setOpen(false);
    const propertyId = n?.data?.propertyId;
    if (propertyId) {
      navigate(`/settings/property-management/view/${encodeURIComponent(propertyId)}`);
    }
  };

  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      <button
        type="button"
        aria-label="Notifications"
        onClick={() => setOpen((p) => !p)}
        style={{ background: "none", border: 0, cursor: "pointer", padding: "0.75rem", position: "relative" }}
      >
        <Notification size={20} />
        {unread > 0 && (
          <span
            style={{
              position: "absolute", top: 4, right: 2, minWidth: 18, height: 18,
              borderRadius: 9, background: "#da1e28", color: "#fff",
              fontSize: 11, lineHeight: "18px", textAlign: "center", padding: "0 4px",
            }}
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

{open && (
  <div
    onScroll={handleScroll}   
    style={{
      position: "absolute", right: 0, top: "100%", width: 360, maxHeight: 420,
      overflowY: "auto", background: "#fff", boxShadow: "0 2px 8px rgba(0,0,0,.25)", zIndex: 9999,
    }}
  >
          <div
            style={{
              padding: "0.75rem 1rem", fontWeight: 600, borderBottom: "1px solid #e0e0e0",
              display: "flex", justifyContent: "space-between", alignItems: "center",
              gap: "0.5rem"
            }}
          >
            <span>Notifications ({unread} unread{total ? ` / ${total}` : ""})</span>

            <div style={{ display: "flex", gap: "0.75rem", alignItems: "center" }}>
              {unread > 0 && (
                <button
                  type="button"
                  onClick={() => markRead(items.map((n) => n.notificationId))}
                  style={{ background: "none", border: 0, color: "#0f62fe", cursor: "pointer", fontSize: 12 }}
                >
                  Mark all as read
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  navigate("/notifications");
                }}
                style={{ background: "none", border: 0, color: "#0f62fe", cursor: "pointer", fontSize: 12, fontWeight: 600 }}
              >
                View all
              </button>
            </div>
          </div>

          {items.length === 0 && (
            <div style={{ padding: "1rem", color: "#6f6f6f" }}>No notifications</div>
          )}

          {items.map((n) => (
            <div
              key={n._id}
              onClick={() => handleItemClick(n)}
              style={{
                padding: "0.75rem 1rem", borderBottom: "1px solid #e0e0e0", cursor: "pointer",
                background: isRead(n) ? "#fff" : "#edf5ff",
              }}
            >
              <div style={{ fontWeight: isRead(n) ? 400 : 600, fontSize: 14 }}>{n.title}</div>
              <div style={{ fontSize: 13, color: "#525252", margin: "2px 0" }}>{n.body}</div>
              <div style={{ fontSize: 12, color: "#8d8d8d" }}>{timeAgo(n.createdAt)}</div>
            </div>
          ))}
          <div ref={sentinelRef} style={{ height: 1 }} />
{loadingMore && (
  <div style={{ padding: "0.75rem", textAlign: "center", color: "#6f6f6f", fontSize: 12 }}>
    Loading…
  </div>
)}
{!loadingMore && hasMore && items.length > 0 && (
  <button
    type="button"
    onClick={() => fetchNotifications(page + 1, { append: true })}
    style={{
      width: "100%", padding: "0.6rem", background: "#f4f4f4",
      border: 0, borderTop: "1px solid #e0e0e0", cursor: "pointer",
      color: "#0f62fe", fontSize: 12,
    }}
  >
    Load more
  </button>
)}
{!hasMore && items.length > 0 && (
  <div style={{ padding: "0.6rem", textAlign: "center", color: "#8d8d8d", fontSize: 12 }}>
    No more notifications
  </div>
)}
        </div>
      )}
    </div>
  );
}